/**
 * The governed catalog of the Vello BI (D520; doc 20 §4.3): every dimension and every metric a screen, a report or an export
 * may ask for, defined ONCE, in terms of the columns of the analytics star (`@vello/analytics-db`, schema `analytics`).
 *
 *  - Identifiers (tables, columns, aliases) exist only here. The planner and the SQL renderer take them from this file and
 *    nowhere else; a request can name a metric or a dimension by id and nothing more, so no request text ever reaches SQL.
 *  - A metric lists a formula PER SOURCE. The hourly aggregates (`agg_*`) are the preferred source; the facts are the
 *    fallback for what the aggregates do not carry (people, items, outcomes, per-order detail).
 *  - Money is bigint minor units + ISO currency and a currency-bound metric is always grouped by currency (never summed
 *    across two). A ratio is Σnumerator ÷ Σdenominator, divided at the very end. A distinct count is non-additive and
 *    internal-only.
 *
 * VOCABULARY CONTRACT with the loader (D519): the status codes below are the values the fact rows carry in `outcome` and
 * `source_kind`. They are constants here (not literals in formulas) so the loader and the catalog are reconciled in one place.
 */
import { operatorsFor, isEnumerable } from './operators.js';
import { isMerchantReviewed } from './merchant-review.js';
/**
 * v2 (D531): the market-standard analyses - stage times, promised against actual ETA, new against returning customers, cohorts, offers, quotes, ratings, coupons,
 * issues, store availability, supply (presence) and demand by pickup cell. A metric or dimension that existed in v1 keeps its own policy version (`since`): only the
 * additions carry v2, so an old answer is never re-labelled.
 */
export const CATALOG_VERSION = 'analytics-semantic-v3';
const V1 = 'analytics-semantic-v1';
const V2 = 'analytics-semantic-v2';
/**
 * The codes the fact tables carry — D519's final contract (the loader writes them; reconcile HERE if it ever names them differently).
 *
 * `fact_order.outcome`: COMPLETED, CANCELLED, NO_DRIVER, IN_PROGRESS, UNKNOWN. Only the first three have a metric of their own; the other
 * two are orders that are still open or whose state the loader could not decide — they count in `orders` (and so in the denominator of
 * the completion and cancellation rates) and nowhere else.
 *
 * `fact_money.source_kind`, and the exact money rule:
 *  - ORDER_BOOKING — the earnings booking of a food order or a delivery;
 *  - RIDE_CASH — a ride paid in cash: the ride's OWN part of the cash collection (fare minus the rider's earlier balance), as the ops
 *    period dashboard (`ops-live-map-composition.ts`, `period`) reads `ride_cash_collections`;
 *  - RIDE_SETTLEMENT — a ride settled digitally, read from `ride_settlements` only when the ride has no cash collection (the dashboard's
 *    `NOT EXISTS (… ride_cash_collections …)`), so a ride is paid through exactly ONE of the two postings;
 *  - PARTNER_CHARGE_AUTHORIZED / _SETTLED / _VOIDED — the partner account's charge; a status change REPLACES the row, the spend is in
 *    `amount_minor`, `gross`/`commission` are 0.
 * gmv, merchandise, delivery fees, discounts, commission, partner net and the take rate sum the REVENUE kinds only (the three first); the
 * partner spend metrics sum their own charge kinds; a VOIDED charge counts nowhere. The hourly aggregates are derived from the revenue
 * kinds by construction, so the aggregate path and the fact path give the same figure.
 *
 * Categories (D521 seam): `service_category` = a ride's service class (`fact_order.category_code` since a0005, and `agg_market_hour.category_code`;
 * the empty code for food, delivery and every money posting - the ETL's rule); `menu_category` = the food item's catalog category (`dim_category` via
 * `fact_order_item`, the one at the time of sale). The old single `category` id is retired.
 */
export const SEMANTIC_VOCABULARY = {
    verticals: ['FOOD', 'DELIVERY', 'RIDE'],
    rideVertical: 'RIDE',
    outcome: { completed: 'COMPLETED', cancelled: 'CANCELLED', noDriver: 'NO_DRIVER', inProgress: 'IN_PROGRESS', unknown: 'UNKNOWN' },
    // `fact_money` holds postings of several kinds; the order's own figures (gross, commission...) live on the BOOKING. A charge posting
    // (the partner's account ledger) may carry the same figures and must never be summed into gmv, commission or the take rate.
    sourceKind: {
        orderBooking: 'ORDER_BOOKING',
        rideCash: 'RIDE_CASH',
        rideSettlement: 'RIDE_SETTLEMENT',
        partnerChargeAuthorized: 'PARTNER_CHARGE_AUTHORIZED',
        partnerChargeSettled: 'PARTNER_CHARGE_SETTLED',
        partnerChargeVoided: 'PARTNER_CHARGE_VOIDED',
    },
    /** The postings that carry an order's or a ride's figures (gross, commission, partner net…). */
    revenueSourceKinds: ['ORDER_BOOKING', 'RIDE_CASH', 'RIDE_SETTLEMENT'],
    // ---- D525 (AN-FACTS2): what the report facts carry, in the words the ETL writes ----
    /**
     * `fact_order.cancel_actor`: WHO cancelled, losslessly - the five ride actors, upper-snake. The coarser `dim_cancel_reason.actor_kind` of the
     * same ride reads CUSTOMER / COURIER / OPS / SYSTEM (a desk agent is `OPS` there): the report that must tell the desk from the operations
     * team reads this column; the root-cause metrics keep reading the dimension.
     */
    cancelActor: { rider: 'RIDER', driver: 'DRIVER', ops: 'OPS', desk: 'DESK', system: 'SYSTEM' },
    /**
     * `fact_order.payment_method`: the ride's snapshotted payment INSTRUMENT token, upper-snake (`cash` -> `CASH`). The set is the market's
     * governed allowed-methods list, so it is open: these are the tokens the platform ships. A ride a partner account pays for still names the
     * instrument; WHO pays is `partner_account_key` / `channel = PARTNER_DESK` (and `dim_payment.payer_kind` on its money rows).
     */
    paymentMethod: { cash: 'CASH', card: 'CARD', instantPayment: 'INSTANT_PAYMENT', account: 'ACCOUNT' },
    /** `fact_offer.status`: the offer lifecycle of `@vello/offer-waves`, upper-snake. */
    offerStatus: { created: 'CREATED', sent: 'SENT', viewed: 'VIEWED', accepted: 'ACCEPTED', rejected: 'REJECTED', expired: 'EXPIRED', superseded: 'SUPERSEDED', cancelled: 'CANCELLED' },
    /** The offer statuses the ride's counters count (`offers_rejected`, `offers_expired`; `offers_sent` counts every offer of the ride). */
    offerCounted: { rejected: 'REJECTED', expired: 'EXPIRED' },
    /**
     * `dim_driver.registration_status` = `drivers.status`, upper-snake. It is the status the registry wrote AT REGISTRATION (`OFFLINE`): no production
     * code path moves it afterwards (live presence is in Redis), so it is NOT an operational status. Blocked / active is the account status `dim_driver.status`.
     */
    registrationStatus: { offline: 'OFFLINE', online: 'ONLINE', onTrip: 'ON_TRIP', suspended: 'SUSPENDED' },
    /** `fact_quote`: the columns a quote keeps. There is NO rider, passenger, person, address or coordinate: a quote is anonymous demand. */
    quoteColumns: ['quote_id', 'category_code', 'created_at', 'currency', 'total_minor', 'distance_meters', 'duration_seconds', 'route_provider_id', 'became_ride', 'ride_id'],
    /** Whether a quote became a ride is the ride's `became_from_quote_id` read back (never a source field of the quote itself). */
    quoteBecameRideFrom: 'fact_order.became_from_quote_id',
    // ---- D530 (AN-CAPTURE): what the capture facts carry, in the words the ETL writes ----
    /**
     * `fact_presence_hour`: seconds per market-local hour in each state a driver's presence can be in, sampled from the hot set (Redis) every slot. The Redis
     * lifecycle words (`@vello/location-sdk`) fold into three buckets: IDLE (available, offered), TO_PICKUP (pickup, waiting), ON_TRIP (trip); offline and paused
     * are not online. `seconds_online = seconds_available + seconds_to_pickup + seconds_on_trip`, at most 3 600 an hour. Utilisation = (to_pickup + on_trip) /
     * online; an hour's coverage = `samples` / (3 600 / `presence_slot.interval_seconds`).
     * CLIENT-DECLARED (review D530 P3-4): the lifecycle word in the hot fix is what the driver app reported (`goOnline`, `acceptOffer`, `startTrip` ...), not something the platform verified - `seconds_to_pickup` / `seconds_on_trip` are the app's claim (a driver who forgets to end a trip still reads on-trip), so they describe declared activity; the ride's own timestamps (`fact_order`) are the platform's record of what happened.
     */
    presence: {
        buckets: { available: 'AVAILABLE', toPickup: 'TO_PICKUP', onTrip: 'ON_TRIP' },
        hotLifecycle: { available: ['available', 'offered'], toPickup: ['pickup', 'waiting'], onTrip: ['trip'] },
        secondsColumns: ['seconds_online', 'seconds_available', 'seconds_to_pickup', 'seconds_on_trip'],
        maxSecondsPerHour: 3600,
    },
    /**
     * The pickup cell of a ride (`fact_order.pickup_cell`, `row:col`): a lat/lng grid with a FIXED reference latitude per market (the same scheme as
     * `@vello/charts-core` `binGeoPoints`), the cell size and the reference latitude (thousandths of a degree) stored beside it. A cell is a QUASI-IDENTIFIER:
     * the store refuses a cell finer than `minMeters`, and a reader that groups by cell and time MUST suppress groups of fewer than `minRiders` distinct riders.
     */
    geoCell: { minMeters: 500, maxMeters: 50000, defaultMeters: 1000, columns: ['pickup_cell', 'pickup_cell_meters', 'pickup_cell_ref_lat_mdeg'], minRiders: 10 },
    /** Promised vs actual: the ETA the dispatch promised on the ACCEPTED offer, the pickup that happened (assignment -> arrival) and the quoted trip (actual trip = `transit_seconds`). */
    etaColumns: ['promised_pickup_seconds', 'actual_pickup_seconds', 'promised_trip_seconds'],
    /** `fact_rating`: who rated whom. A RIDER rates the DRIVER; a DRIVER rates the RIDER. Stars 1-5 and neutral tag tokens only - never free text. */
    raterRole: { rider: 'RIDER', driver: 'DRIVER' },
    ratingSubject: { driver: 'DRIVER', rider: 'RIDER' },
    /** `fact_promo_redemption`: who bears the discount. A coupon is the platform's cost (D461 section 5 #13); MERCHANT is reserved for merchant promotions, which record no redemption. */
    funder: { platform: 'PLATFORM', merchant: 'MERCHANT' },
    couponSource: { operator: 'OPERATOR', referral: 'REFERRAL', campaign: 'CAMPAIGN' },
    /** `fact_issue`: the two record kinds, the approval kinds (`refund_approvals.kind`) and the post-sale kinds (`food_postsale_cases.kind`), upper-snake. */
    issueSourceKind: { refundApproval: 'REFUND_APPROVAL', postsaleCase: 'POSTSALE_CASE' },
    refundApprovalKind: ['FOOD_REFUND', 'FOOD_CHARGEBACK', 'SUPPORT_REFUND', 'SUPPORT_COUPON', 'PARTNER_CREDIT', 'DISPUTE_CREDIT'],
    postSaleKind: ['CANCELLATION', 'REFUND', 'DISPUTE'],
    /** `fact_store_availability.state`: OPEN / BUSY / PAUSED while a store's profile is active; DRAFT / INACTIVE while it is not live. */
    storeAvailabilityState: { open: 'OPEN', busy: 'BUSY', paused: 'PAUSED', draft: 'DRAFT', inactive: 'INACTIVE' },
    // ---- D536 (AN-RIDER-DEBTS): what the rider debt facts carry, in the words the ETL writes ----
    /**
     * `fact_rider_debt` (one row per debt of a rider, D468) and `fact_rider_debt_payment` (one per ride that collected part of it). Origin = the kind of debt, status = how
     * much is paid; nothing is ever written off in the current rules, so there is no WRITTEN_OFF. The aging buckets are the age of an OPEN debt in whole days since it
     * arose, as of the moment of the read (`agg_rider_debt_aging` computes them in the store). Both facts are person-level (PERSONAL role only); the plain reader sees
     * the three `agg_rider_debt_*` views, which carry no rider key and no ride id. The debt screens are a dedicated surface, not a catalog metric: the aging is "as of now".
     */
    riderDebt: {
        origins: ['CASH_SHORTFALL', 'CANCELLATION_FEE', 'OTHER'],
        statuses: ['OPEN', 'PARTIAL', 'SETTLED'],
        agingBuckets: [
            { id: 'D0_7', fromDays: 0, toDays: 7 },
            { id: 'D8_30', fromDays: 8, toDays: 30 },
            { id: 'D31_60', fromDays: 31, toDays: 60 },
            { id: 'D61_PLUS', fromDays: 61, toDays: null },
        ],
        views: ['agg_rider_debt_market_day', 'agg_rider_debt_aging', 'agg_rider_debt_open'],
        personalTables: ['fact_rider_debt', 'fact_rider_debt_payment'],
    },
    /** The columns the capture facts add to `fact_money`: the ledger's stable reason for the posting and the earnings booking kind. */
    moneyReasonColumns: ['reason_code', 'booking_kind'],
    // ---- D531 (AN-ADMIN-INSIGHTS): the rules of the market-standard analyses, named once ----
    /**
     * On time: the pickup that happened (`actual_pickup_seconds`) came no later than the one the accepted offer promised (`promised_pickup_seconds`) plus a
     * GRACE. A governed constant of the catalog (versioned with it), not a setting: changing it changes the meaning of the metric and bumps its version.
     * Both numbers must exist; an order missing either is not compared. Food has no promised time (NC in D530), so the metric reads rides and deliveries.
     */
    eta: { onTimeGraceSeconds: 60 },
    /** The bucket edges of the distributions, in seconds (the last bucket is open: `>= last edge`). The percentile of a bucketed distribution is an approximation. */
    buckets: {
        matchSeconds: [15, 30, 45, 60, 90, 120, 180, 300],
        pickupSeconds: [120, 300, 480, 600, 900, 1200],
        /** actual - promised: negative = earlier than promised. */
        etaDeviationSeconds: [-300, -120, -60, 0, 60, 120, 300, 600],
        /** The hour of the day in two-hour shifts (00-02, 02-04 ... 22-24). */
        shiftHours: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22],
        // D533 (the merchant's logistics): the stages of a food order, in seconds. The last bucket of each is open.
        prepSeconds: [300, 600, 900, 1200, 1500, 1800, 2400],
        pickupWaitSeconds: [120, 300, 600, 900, 1200, 1800],
        transitSeconds: [300, 600, 900, 1200, 1800, 2700],
        totalSeconds: [1200, 1800, 2400, 3000, 3600, 4500],
    },
    /**
     * D533: what a store calls a LATE order when no promised time exists for food: a total time (request to delivery) of at least `lateTotalSeconds`, or a wait at
     * the counter (ready to picked up) of at least `lateWaitSeconds`. A governed constant of the catalog, read off the histogram buckets (the screen says so); the
     * promised time of a food order is not captured (D530 "Food: NC").
     */
    late: { lateTotalSeconds: 3600, lateWaitSeconds: 600 },
    /** `fact_rating.stars` at or below this value is a low rating. */
    lowRatingStars: ['1', '2'],
    /** `fact_offer.status` of an offer the driver DECIDED on (or let lapse): the denominator of the acceptance rate. */
    offerDecided: ['ACCEPTED', 'REJECTED', 'EXPIRED'],
    /** `fact_issue.kind` that is a refund (an approval or a post-sale case). */
    refundKinds: ['FOOD_REFUND', 'SUPPORT_REFUND', 'REFUND'],
    /** `fact_store_availability.state` of a LIVE store (the other two are not live), and of one that is taking orders. */
    storeLiveStates: ['OPEN', 'BUSY', 'PAUSED'],
    storeOpenStates: ['OPEN', 'BUSY'],
    /** Privacy floors the views enforce (the catalog states them so a screen can say them): drivers per published hour, riders per published cell-day. */
    floors: { presenceDrivers: 5, pickupCellRiders: 10 },
};
// ───────────────────────────── sources ─────────────────────────────
const MEASURE_COLUMNS = [
    'orders_count', 'completed_count', 'cancelled_count', 'items_count', 'gross_minor', 'merchandise_minor', 'delivery_minor',
    'discount_minor', 'commission_minor', 'partner_net_minor', 'amount_minor', 'prep_seconds_sum', 'prep_samples',
    'total_seconds_sum', 'total_samples', 'distance_meters_sum',
];
/** The columns each source table has (a self-test pins every catalog reference to this list; migrations a0002/a0003). */
export const SOURCE_COLUMNS = {
    agg_market_hour: ['market_id', 'category_code', 'vertical', 'date_key', 'hour_key', 'hour_occurrence', 'currency', ...MEASURE_COLUMNS],
    agg_merchant_hour: ['market_id', 'merchant_ref', 'location_ref', 'date_key', 'hour_key', 'hour_occurrence', 'currency', ...MEASURE_COLUMNS],
    agg_partner_hour: ['market_id', 'account_ref', 'vertical', 'date_key', 'hour_key', 'hour_occurrence', 'currency', 'timezone', ...MEASURE_COLUMNS],
    fact_order: [
        'market_id', 'vertical', 'order_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'channel', 'status', 'outcome',
        'customer_key', 'driver_key', 'courier_key', 'merchant_key', 'location_key', 'partner_account_key', 'payment_key',
        'cancel_reason_key', 'items_count', 'accept_seconds', 'prep_seconds', 'transit_seconds', 'total_seconds', 'distance_meters', 'category_code',
        // D525 (a0008): the ride's report fields
        'order_code', 'became_from_quote_id', 'quoted_minor', 'final_minor', 'final_reason_code', 'currency', 'payment_method', 'cancel_actor',
        'offers_sent', 'offers_rejected', 'offers_expired', 'created_by_operator_key',
        // D530 (a0011): promised vs actual ETA and the coarse pickup cell
        'promised_pickup_seconds', 'actual_pickup_seconds', 'promised_trip_seconds', 'pickup_cell', 'pickup_cell_meters', 'pickup_cell_ref_lat_mdeg',
        // D533 (a0014): the food order's items value (minor units of `currency`) and the wait at the counter (ready -> picked up)
        'order_value_minor', 'pickup_wait_seconds',
    ],
    fact_money: [
        'market_id', 'source_kind', 'source_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'vertical', 'order_id',
        'customer_key', 'merchant_key', 'location_key', 'partner_account_key', 'payment_key', 'currency', 'gross_minor',
        'merchandise_minor', 'delivery_minor', 'discount_minor', 'commission_minor', 'partner_net_minor', 'amount_minor',
        // D530 (a0011): the ledger's stable reason for the posting
        'reason_code', 'booking_kind',
    ],
    fact_order_item: [
        'market_id', 'vertical', 'order_id', 'line_no', 'date_key', 'timezone', 'merchant_key', 'location_key', 'item_key',
        'category_key', 'quantity', 'currency', 'unit_minor', 'amount_minor',
        // D533 (a0014): the order's customer (the people floor of the sold lines), the order's outcome at the time the lines were last written and its local hour
        'customer_key', 'outcome', 'hour_key', 'hour_occurrence',
    ],
    // ---- D531: the report / capture facts and the two privacy-floored views ----
    fact_offer: [
        'market_id', 'offer_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'ride_id', 'driver_key', 'wave_number', 'status', 'channel',
        'sent_at', 'responded_at', 'reject_reason_code', 'pickup_eta_seconds', 'pickup_distance_meters',
    ],
    fact_quote: [
        'market_id', 'quote_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'category_code', 'currency', 'total_minor', 'distance_meters',
        'duration_seconds', 'route_provider_id', 'became_ride', 'ride_id',
    ],
    fact_rating: [
        'market_id', 'rating_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'vertical', 'order_id', 'rater_role', 'subject_kind', 'driver_key',
        'customer_key', 'stars',
    ],
    fact_promo_redemption: [
        'market_id', 'redemption_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'source_kind', 'vertical', 'order_id', 'quote_id', 'coupon_id',
        'campaign', 'coupon_source', 'funder', 'customer_key', 'currency', 'gross_minor', 'discount_minor', 'subsidy_minor', 'reason_code',
    ],
    fact_issue: [
        'market_id', 'issue_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'source_kind', 'kind', 'vertical', 'order_id', 'merchant_key',
        'location_key', 'status', 'reason_code', 'currency', 'amount_minor', 'resolved_at',
    ],
    fact_store_availability: [
        'market_id', 'interval_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'merchant_key', 'location_key', 'state', 'seconds',
    ],
    /** A VIEW (a0010): online time of a market by local hour, no driver in it, an hour published from five drivers on. */
    agg_presence_market_hour: [
        'market_id', 'date_key', 'hour_key', 'hour_occurrence', 'timezone', 'drivers_online', 'seconds_online', 'seconds_available', 'seconds_to_pickup',
        'seconds_on_trip', 'samples',
    ],
    /** A VIEW (a0011): ride pickups by market, local day and coarse cell, a cell-day published from ten distinct riders on. */
    agg_pickup_cell_day: ['market_id', 'date_key', 'pickup_cell', 'pickup_cell_meters', 'pickup_cell_ref_lat_mdeg', 'orders_count', 'riders_count'],
};
/** The sources that are VIEWS of the star (their columns are checked against the view definition, not a table). */
export const VIEW_SOURCES = ['agg_presence_market_hour', 'agg_pickup_cell_day'];
export function isAggSource(source) {
    return source.startsWith('agg_');
}
export function hasColumn(source, col) {
    return SOURCE_COLUMNS[source].includes(col);
}
/**
 * Whether the "up to now" comparison cuts a source down to the HOUR (else to the day). The sold lines have carried an hour since a0014, but a line written before it has
 * none (NULL until the next rebuild), and a cut by a NULL hour would drop today's old lines: they stay at day grain, as they were.
 */
export function capsByHour(source) {
    return hasColumn(source, 'hour_key') && source !== 'fact_order_item';
}
export const JOINS = {
    market: { table: 'dim_market', alias: 'mk', kind: 'inner', on: [['market_id', 'market_id']] },
    hour: { table: 'dim_hour', alias: 'dh', kind: 'inner', on: [['hour_key', 'hour_key']] },
    customer: { table: 'dim_customer', alias: 'dc', kind: 'left', on: [['market_id', 'market_id'], ['customer_key', 'customer_key']] },
    driver: { table: 'dim_driver', alias: 'dr', kind: 'left', on: [['market_id', 'market_id'], ['driver_key', 'driver_key']] },
    courier: { table: 'dim_courier', alias: 'dk', kind: 'left', on: [['market_id', 'market_id'], ['courier_key', 'courier_key']] },
    // D528: the market calendar row of the source row's OWN date (the holiday flag); a date the calendar lacks simply has no row.
    date: { table: 'dim_date', alias: 'dt', kind: 'left', on: [['market_id', 'market_id'], ['date_key', 'date_key']] },
    merchant: { table: 'dim_merchant', alias: 'dm', kind: 'left', on: [['market_id', 'market_id'], ['merchant_key', 'merchant_key']] },
    location: { table: 'dim_location', alias: 'dl', kind: 'left', on: [['market_id', 'market_id'], ['location_key', 'location_key']] },
    partner: { table: 'dim_partner_account', alias: 'dp', kind: 'left', on: [['market_id', 'market_id'], ['partner_account_key', 'partner_account_key']] },
    payment: { table: 'dim_payment', alias: 'dy', kind: 'left', on: [['market_id', 'market_id'], ['payment_key', 'payment_key']] },
    cancel: { table: 'dim_cancel_reason', alias: 'dx', kind: 'left', on: [['market_id', 'market_id'], ['cancel_reason_key', 'cancel_reason_key']] },
    item: { table: 'dim_item', alias: 'di', kind: 'left', on: [['market_id', 'market_id'], ['item_key', 'item_key']] },
    category: { table: 'dim_category', alias: 'dg', kind: 'left', on: [['market_id', 'market_id'], ['category_key', 'category_key']] },
};
/** The columns of the dimension tables the catalog reads (a self-test pins them to the migrations' tables). */
export const DIM_TABLE_COLUMNS = {
    dim_market: ['market_id', 'country_code', 'region_code', 'market_name', 'timezone', 'currency'],
    // F3 (a0020): the states' names - a GLOBAL table (no market_id), read only by the label lookup of the `region` dimension
    dim_region: ['region_code', 'country_code', 'region_name'],
    dim_hour: ['hour_key', 'day_part'],
    dim_date: ['market_id', 'date_key', 'is_holiday', 'holiday_name', 'same_date_last_year', 'same_weekday_last_year'],
    dim_customer: ['market_id', 'customer_key', 'natural_key', 'pseudonymous_key', 'display_name', 'phone_masked', 'city', 'first_order_date'],
    dim_driver: ['market_id', 'driver_key', 'natural_key', 'pseudonymous_key', 'display_name', 'phone_masked', 'city', 'fleet_id', 'fleet_name', 'is_current'],
    dim_courier: ['market_id', 'courier_key', 'natural_key', 'pseudonymous_key', 'display_name', 'phone_masked', 'city', 'fleet_name'],
    dim_merchant: ['market_id', 'merchant_key', 'natural_key', 'display_name', 'category_code', 'category_name', 'is_current'],
    dim_location: ['market_id', 'location_key', 'natural_key', 'display_name', 'is_current'],
    dim_partner_account: ['market_id', 'partner_account_key', 'natural_key', 'cost_center_code', 'display_name', 'cost_center_name'],
    dim_payment: ['market_id', 'payment_key', 'method_code', 'payer_kind'],
    dim_cancel_reason: ['market_id', 'cancel_reason_key', 'reason_code', 'actor_kind'],
    dim_item: ['market_id', 'item_key', 'natural_key', 'merchant_natural_key', 'display_name', 'is_current'],
    dim_category: ['market_id', 'category_key', 'natural_key', 'display_name'],
};
// ───────────────────────────── dimensions ─────────────────────────────
const col = (c) => ({ kind: 'col', col: c });
const via = (join, c) => ({ kind: 'via', join, col: c });
const time = (part) => ({ kind: 'time', part });
/** D531: the report / capture facts (each carries `date_key`, the hour and the market, so every time dimension reads them). */
const NEW_FACTS = ['fact_offer', 'fact_quote', 'fact_rating', 'fact_promo_redemption', 'fact_issue', 'fact_store_availability'];
const ALL_SOURCES = ['agg_market_hour', 'agg_merchant_hour', 'agg_partner_hour', 'fact_order', 'fact_money', 'fact_order_item', ...NEW_FACTS, 'agg_presence_market_hour', 'agg_pickup_cell_day'];
const WITH_HOUR = ['agg_market_hour', 'agg_merchant_hour', 'agg_partner_hour', 'fact_order', 'fact_money', 'fact_order_item', ...NEW_FACTS, 'agg_presence_market_hour'];
const WITH_CURRENCY = ['agg_market_hour', 'agg_merchant_hour', 'agg_partner_hour', 'fact_money', 'fact_order_item', 'fact_quote', 'fact_promo_redemption', 'fact_issue'];
const WITH_VERTICAL = ['agg_market_hour', 'agg_partner_hour', 'fact_order', 'fact_money', 'fact_order_item', 'fact_rating', 'fact_promo_redemption', 'fact_issue'];
/** Facts that carry the merchant / location / partner / payment keys. */
const FACTS_MERCHANT = ['fact_order', 'fact_money', 'fact_order_item', 'fact_issue', 'fact_store_availability'];
const FACTS_PARTNER = ['fact_order', 'fact_money'];
function across(sources, def) {
    const out = {};
    for (const s of sources)
        out[s] = def;
    return out;
}
const dim = (d) => d;
/**
 * D528: a RECORD-LEVEL number of a fact row, offered for filtering only (a range over the order's own distance, a posting's own amount...).
 * The aggregates hold sums per hour, not per record, so these live on the facts and make the planner take the fact path. Money is a
 * dimension in minor units of the ROW's currency: its conditions always name the currency.
 */
const recordNumber = (id, valueType, source, column) => dim({ id, labelKey: `analytics.dim.${id}`, kind: 'attribute', personal: false, valueType, filterOnly: true, recordLevel: true, sources: { [source]: { key: col(column) } } });
const V = SEMANTIC_VOCABULARY;
/** D531: a dimension of the new analyses: one key on one or more sources, platform admin only, never a person. */
const adminDim = (id, kind, valueType, on, key) => dim({ id, labelKey: `analytics.dim.${id}`, kind, personal: false, valueType, adminOnly: true, sources: across(on, { key }) });
/** D533: a dimension of the store's portal - the same shape as {@link adminDim}; what makes it reachable by a merchant is its row in `merchant-review.ts`. */
const merchantDim = adminDim;
export const DIMENSIONS = [
    // ---- time: year → month → day → hour; quarter and weekday sit beside the chain ----
    dim({ id: 'year', labelKey: 'analytics.dim.year', kind: 'time', personal: false, valueType: 'year', hierarchy: { chain: 'time', level: 0, child: 'month' }, sources: across(ALL_SOURCES, { key: time('year') }) }),
    dim({ id: 'quarter', labelKey: 'analytics.dim.quarter', kind: 'time', personal: false, valueType: 'quarter', sources: across(ALL_SOURCES, { key: time('quarter') }) }),
    dim({ id: 'month', labelKey: 'analytics.dim.month', kind: 'time', personal: false, valueType: 'month', hierarchy: { chain: 'time', level: 1, child: 'day' }, sources: across(ALL_SOURCES, { key: time('month') }) }),
    dim({ id: 'day', labelKey: 'analytics.dim.day', kind: 'time', personal: false, valueType: 'day', hierarchy: { chain: 'time', level: 2, child: 'hour' }, sources: across(ALL_SOURCES, { key: time('day') }) }),
    dim({ id: 'hour', labelKey: 'analytics.dim.hour', kind: 'time', personal: false, valueType: 'hour', hierarchy: { chain: 'time', level: 3 }, sources: across(WITH_HOUR, { key: time('hour') }) }),
    dim({ id: 'weekday', labelKey: 'analytics.dim.weekday', kind: 'time', personal: false, valueType: 'weekday', sources: across(ALL_SOURCES, { key: time('weekday') }) }),
    dim({ id: 'day_part', labelKey: 'analytics.dim.day_part', kind: 'time', personal: false, valueType: 'dayPart', sources: across(WITH_HOUR, { key: { kind: 'dayPart' } }) }),
    // D528: the market calendar's holiday flag of the row's own date ('true' / 'false'); a date with no calendar row is not a holiday.
    dim({ id: 'holiday', labelKey: 'analytics.dim.holiday', kind: 'time', personal: false, valueType: 'flag', sources: across(ALL_SOURCES, { key: { kind: 'holiday' } }) }),
    // ---- geography: country → region (state) → market ----
    dim({ id: 'country', labelKey: 'analytics.dim.country', kind: 'geo', personal: false, valueType: 'country', hierarchy: { chain: 'geo', level: 0, child: 'region' }, sources: across(ALL_SOURCES, { key: via('market', 'country_code') }) }),
    // F3 (ADR-0124, a0020): the state of the row's market (`dim_market.region_code`, NULL for a market with no state) with its registered name looked up after the page
    // (`dim_region`, a global table: no market on the lookup). Platform-admin only like the other analyses of the operation: a store or a partner never groups by state.
    dim({
        id: 'region',
        labelKey: 'analytics.dim.region',
        kind: 'geo',
        personal: false,
        valueType: 'region',
        adminOnly: true,
        hierarchy: { chain: 'geo', level: 1, child: 'market' },
        sources: across(ALL_SOURCES, { key: via('market', 'region_code'), lookup: { table: 'dim_region', keyCol: 'region_code', labelCol: 'region_name', marketScoped: false } }),
    }),
    dim({
        id: 'market',
        labelKey: 'analytics.dim.market',
        kind: 'geo',
        personal: false,
        valueType: 'market',
        hierarchy: { chain: 'geo', level: 2 },
        sources: across(ALL_SOURCES, { key: col('market_id'), lookup: { table: 'dim_market', keyCol: 'market_id', labelCol: 'market_name', marketScoped: false } }),
    }),
    // ---- attributes of the rows themselves ----
    dim({ id: 'vertical', labelKey: 'analytics.dim.vertical', kind: 'attribute', personal: false, valueType: 'vertical', sources: across(WITH_VERTICAL, { key: col('vertical') }) }),
    dim({ id: 'currency', labelKey: 'analytics.dim.currency', kind: 'attribute', personal: false, valueType: 'currency', sources: across(WITH_CURRENCY, { key: col('currency') }) }),
    dim({ id: 'channel', labelKey: 'analytics.dim.channel', kind: 'attribute', personal: false, valueType: 'code', sources: { fact_order: { key: col('channel') } } }),
    dim({ id: 'status', labelKey: 'analytics.dim.status', kind: 'attribute', personal: false, valueType: 'code', sources: { fact_order: { key: col('status') } } }),
    dim({ id: 'outcome', labelKey: 'analytics.dim.outcome', kind: 'attribute', personal: false, valueType: 'code', sources: { fact_order: { key: col('outcome') } } }),
    dim({ id: 'source_kind', labelKey: 'analytics.dim.source_kind', kind: 'attribute', personal: false, valueType: 'code', sources: { fact_money: { key: col('source_kind') } } }),
    // ---- entities: merchant → location, category → item, person → order ----
    dim({
        id: 'merchant',
        labelKey: 'analytics.dim.merchant',
        kind: 'entity',
        personal: false,
        valueType: 'ref',
        hierarchy: { chain: 'merchant', level: 0, child: 'location' },
        sources: {
            agg_merchant_hour: { key: col('merchant_ref'), marketScoped: true, lookup: { table: 'dim_merchant', keyCol: 'natural_key', labelCol: 'display_name', currentOnly: true, marketScoped: true } },
            ...across(FACTS_MERCHANT, { key: via('merchant', 'natural_key'), label: via('merchant', 'display_name'), marketScoped: true }),
        },
    }),
    dim({
        id: 'location',
        labelKey: 'analytics.dim.location',
        kind: 'entity',
        personal: false,
        valueType: 'ref',
        hierarchy: { chain: 'merchant', level: 1 },
        sources: {
            agg_merchant_hour: { key: col('location_ref'), marketScoped: true, lookup: { table: 'dim_location', keyCol: 'natural_key', labelCol: 'display_name', currentOnly: true, marketScoped: true } },
            ...across(FACTS_MERCHANT, { key: via('location', 'natural_key'), label: via('location', 'display_name'), marketScoped: true }),
        },
    }),
    dim({ id: 'merchant_category', labelKey: 'analytics.dim.merchant_category', kind: 'attribute', personal: false, valueType: 'ref', sources: across(FACTS_MERCHANT, { key: via('merchant', 'category_code'), label: via('merchant', 'category_name') }) }),
    dim({
        id: 'partner_account',
        labelKey: 'analytics.dim.partner_account',
        kind: 'entity',
        personal: false,
        valueType: 'ref',
        sources: {
            agg_partner_hour: { key: col('account_ref'), marketScoped: true, lookup: { table: 'dim_partner_account', keyCol: 'natural_key', labelCol: 'display_name', marketScoped: true, orderByCol: 'cost_center_code' } },
            ...across(FACTS_PARTNER, { key: via('partner', 'natural_key'), label: via('partner', 'display_name'), marketScoped: true }),
        },
    }),
    dim({ id: 'cost_center', labelKey: 'analytics.dim.cost_center', kind: 'attribute', personal: false, valueType: 'ref', sources: across(FACTS_PARTNER, { key: via('partner', 'cost_center_code'), label: via('partner', 'cost_center_name'), marketScoped: true }) }),
    // Two different things were one word. SERVICE category = the class of a ride or delivery service (`standard`, `comfort`, a market-defined slug the
    // source system owns): `fact_order.category_code` and `agg_market_hour.category_code`; the empty code is a vertical with none (food, delivery) and money
    // postings. MENU category = a food catalog category an item belongs to (category -> item), the one AT THE TIME OF SALE: `dim_category` through
    // `fact_order_item`. They share no source, so they are never combinable.
    dim({
        id: 'service_category',
        labelKey: 'analytics.dim.service_category',
        kind: 'attribute',
        personal: false,
        valueType: 'code',
        sources: {
            agg_market_hour: { key: col('category_code') },
            fact_order: { key: { kind: 'col', col: 'category_code', orEmpty: true } },
            fact_quote: { key: col('category_code') },
        },
    }),
    dim({
        id: 'menu_category',
        labelKey: 'analytics.dim.menu_category',
        kind: 'entity',
        personal: false,
        valueType: 'ref',
        hierarchy: { chain: 'catalog', level: 0, child: 'item' },
        sources: {
            fact_order_item: { key: via('category', 'natural_key'), label: via('category', 'display_name'), marketScoped: true },
        },
    }),
    dim({
        id: 'item',
        labelKey: 'analytics.dim.item',
        kind: 'entity',
        personal: false,
        valueType: 'ref',
        hierarchy: { chain: 'catalog', level: 1 },
        sources: { fact_order_item: { key: via('item', 'natural_key'), label: via('item', 'display_name'), marketScoped: true } },
    }),
    dim({ id: 'payment_method', labelKey: 'analytics.dim.payment_method', kind: 'attribute', personal: false, valueType: 'code', sources: across(FACTS_PARTNER, { key: via('payment', 'method_code') }) }),
    dim({ id: 'payer_kind', labelKey: 'analytics.dim.payer_kind', kind: 'attribute', personal: false, valueType: 'code', sources: across(FACTS_PARTNER, { key: via('payment', 'payer_kind') }) }),
    dim({ id: 'cancel_reason', labelKey: 'analytics.dim.cancel_reason', kind: 'attribute', personal: false, valueType: 'code', sources: { fact_order: { key: via('cancel', 'reason_code') } } }),
    dim({ id: 'cancel_actor', labelKey: 'analytics.dim.cancel_actor', kind: 'attribute', personal: false, valueType: 'code', sources: { fact_order: { key: via('cancel', 'actor_kind') } } }),
    dim({ id: 'fleet', labelKey: 'analytics.dim.fleet', kind: 'attribute', personal: false, valueType: 'ref', sources: { fact_order: { key: via('driver', 'fleet_id'), label: via('driver', 'fleet_name'), marketScoped: true } } }),
    // ---- people (personal: the key is a pseudonym, the label is the name) → order ----
    dim({
        id: 'customer',
        labelKey: 'analytics.dim.customer',
        kind: 'entity',
        personal: true,
        valueType: 'ref',
        hierarchy: { chain: 'person', level: 0, child: 'order' },
        sources: across(['fact_order', 'fact_money', 'fact_rating', 'fact_promo_redemption'], { key: via('customer', 'pseudonymous_key'), label: via('customer', 'display_name'), marketScoped: true }),
    }),
    dim({
        id: 'driver',
        labelKey: 'analytics.dim.driver',
        kind: 'entity',
        personal: true,
        valueType: 'ref',
        hierarchy: { chain: 'person', level: 0, child: 'order' },
        sources: across(['fact_order', 'fact_offer', 'fact_rating'], { key: via('driver', 'pseudonymous_key'), label: via('driver', 'display_name'), marketScoped: true }),
    }),
    dim({
        id: 'courier',
        labelKey: 'analytics.dim.courier',
        kind: 'entity',
        personal: true,
        valueType: 'ref',
        hierarchy: { chain: 'person', level: 0, child: 'order' },
        sources: { fact_order: { key: via('courier', 'pseudonymous_key'), label: via('courier', 'display_name'), marketScoped: true } },
    }),
    // The ORDER grain is personal (D520 review P2-1): an order id is one person's transaction. Two known order ids in a filter of
    // `active_customers` would say whether they are the same person; as a dimension it lists transactions one by one. So it needs
    // `analytics:view_personal` and is access-logged, like the people it leads to.
    dim({
        id: 'order',
        labelKey: 'analytics.dim.order',
        kind: 'entity',
        personal: true,
        valueType: 'orderRef',
        hierarchy: { chain: 'person', level: 1 },
        sources: across(['fact_order', 'fact_money', 'fact_order_item'], { key: { kind: 'orderRef' }, marketScoped: true }),
    }),
    // ---- D528: record-level numbers (filter-only; the unit is the column's: items, minor units, seconds, meters) ----
    recordNumber('order_items', 'count', 'fact_order', 'items_count'),
    recordNumber('order_distance', 'distance', 'fact_order', 'distance_meters'),
    recordNumber('order_duration', 'duration', 'fact_order', 'total_seconds'),
    recordNumber('prep_duration', 'duration', 'fact_order', 'prep_seconds'),
    recordNumber('accept_duration', 'duration', 'fact_order', 'accept_seconds'),
    recordNumber('posting_gross', 'money', 'fact_money', 'gross_minor'),
    recordNumber('item_unit_price', 'money', 'fact_order_item', 'unit_minor'),
    recordNumber('item_line_amount', 'money', 'fact_order_item', 'amount_minor'),
    // ---- D531: the dimensions of the market-standard analyses (platform admin only until D533 reviews them for a partner) ----
    adminDim('shift', 'time', 'digits', WITH_HOUR, { kind: 'bucket', of: 'hour_key', edges: V.buckets.shiftHours }),
    adminDim('match_time_bucket', 'attribute', 'digits', ['fact_order'], { kind: 'bucket', of: 'accept_seconds', edges: V.buckets.matchSeconds }),
    adminDim('pickup_time_bucket', 'attribute', 'digits', ['fact_order'], { kind: 'bucket', of: 'actual_pickup_seconds', edges: V.buckets.pickupSeconds }),
    adminDim('eta_deviation_bucket', 'attribute', 'digits', ['fact_order'], { kind: 'bucket', of: ['actual_pickup_seconds', 'promised_pickup_seconds'], edges: V.buckets.etaDeviationSeconds }),
    adminDim('customer_type', 'attribute', 'code', ['fact_order'], { kind: 'customerType' }),
    adminDim('cohort_month', 'attribute', 'month', ['fact_order'], { kind: 'cohort', part: 'month' }),
    adminDim('cohort_age', 'attribute', 'digits', ['fact_order'], { kind: 'cohort', part: 'age' }),
    adminDim('offer_status', 'attribute', 'code', ['fact_offer'], col('status')),
    adminDim('offer_channel', 'attribute', 'code', ['fact_offer'], col('channel')),
    adminDim('offer_wave', 'attribute', 'digits', ['fact_offer'], col('wave_number')),
    adminDim('offer_reject_reason', 'attribute', 'code', ['fact_offer'], col('reject_reason_code')),
    adminDim('quote_became_ride', 'attribute', 'flag', ['fact_quote'], col('became_ride')),
    adminDim('route_provider', 'attribute', 'ref', ['fact_quote'], col('route_provider_id')),
    adminDim('stars', 'attribute', 'digits', ['fact_rating'], col('stars')),
    adminDim('rater_role', 'attribute', 'code', ['fact_rating'], col('rater_role')),
    adminDim('rating_subject', 'attribute', 'code', ['fact_rating'], col('subject_kind')),
    adminDim('coupon', 'attribute', 'ref', ['fact_promo_redemption'], col('coupon_id')),
    adminDim('campaign', 'attribute', 'ref', ['fact_promo_redemption'], col('campaign')),
    adminDim('coupon_source', 'attribute', 'code', ['fact_promo_redemption'], col('coupon_source')),
    adminDim('funder', 'attribute', 'code', ['fact_promo_redemption'], col('funder')),
    adminDim('issue_kind', 'attribute', 'code', ['fact_issue'], col('kind')),
    adminDim('issue_source', 'attribute', 'code', ['fact_issue'], col('source_kind')),
    adminDim('issue_status', 'attribute', 'code', ['fact_issue'], col('status')),
    adminDim('issue_reason', 'attribute', 'code', ['fact_issue'], col('reason_code')),
    adminDim('availability_state', 'attribute', 'code', ['fact_store_availability'], col('state')),
    // The pickup cell reaches the reader ONLY through the k-floored view: `fact_order` hides the cell columns from the plain reader. The key carries the cell, its size and its
    // reference latitude (`row:col:meters:refLatMdeg`) so it alone rebuilds the bounds (`pickupCellBounds`).
    adminDim('pickup_cell', 'geo', 'ref', ['agg_pickup_cell_day'], { kind: 'concat', cols: ['pickup_cell', 'pickup_cell_meters', 'pickup_cell_ref_lat_mdeg'] }),
    // ---- D533: the store's portal (each admitted for a MERCHANT tenant by `merchant-review.ts`) ----
    merchantDim('merchant_customer_type', 'attribute', 'code', ['fact_order'], { kind: 'merchantCustomerType' }),
    merchantDim('prep_time_bucket', 'attribute', 'digits', ['fact_order'], { kind: 'bucket', of: 'prep_seconds', edges: V.buckets.prepSeconds }),
    merchantDim('pickup_wait_bucket', 'attribute', 'digits', ['fact_order'], { kind: 'bucket', of: 'pickup_wait_seconds', edges: V.buckets.pickupWaitSeconds }),
    merchantDim('delivery_time_bucket', 'attribute', 'digits', ['fact_order'], { kind: 'bucket', of: 'transit_seconds', edges: V.buckets.transitSeconds }),
    merchantDim('total_time_bucket', 'attribute', 'digits', ['fact_order'], { kind: 'bucket', of: 'total_seconds', edges: V.buckets.totalSeconds }),
    dim({
        id: 'owned_item',
        labelKey: 'analytics.dim.owned_item',
        kind: 'entity',
        personal: false,
        valueType: 'ref',
        adminOnly: true,
        sources: { fact_order_item: { key: { kind: 'ownedItem', col: 'natural_key' }, label: { kind: 'ownedItem', col: 'display_name' }, marketScoped: true } },
    }),
];
const DIMENSION_INDEX = new Map(DIMENSIONS.map((d) => [d.id, d]));
export function getDimension(id) {
    return DIMENSION_INDEX.get(id);
}
/** The dimension ids that a drill from `id` goes down to (one level), if the chain has one. */
export function drillChild(id) {
    return getDimension(id)?.hierarchy?.child;
}
// ───────────────────────────── metrics ─────────────────────────────
const eq = (c, value) => ({ col: c, eq: value });
const all = (...preds) => ({ all: preds });
const isRide = eq('vertical', V.rideVertical);
/** The postings that carry an order's or a ride's figures. The aggregates are derived from exactly these, so they need no filter. */
const isBooking = { col: 'source_kind', in: V.revenueSourceKinds };
const sum = (c, where) => ({ fn: 'sum', col: c, ...(where !== undefined ? { where } : {}) });
const count = (where, c) => ({ fn: 'count', ...(c !== undefined ? { col: c } : {}), ...(where !== undefined ? { where } : {}) });
const single = (piece) => ({ kind: 'single', piece });
const ratio = (num, den) => ({ kind: 'ratio', num, den });
function sources(map) {
    return map;
}
function onAggs(formula, which = ['agg_market_hour', 'agg_merchant_hour', 'agg_partner_hour']) {
    const out = {};
    for (const s of which)
        out[s] = formula;
    return out;
}
const AGG_WITH_VERTICAL = ['agg_market_hour', 'agg_partner_hour'];
function metric(m) {
    const { since, ...rest } = m;
    return {
        ...rest,
        labelKey: `analytics.metric.${m.id}`,
        descriptionKey: `analytics.metric.${m.id}.description`,
        currencyBound: m.currencyBound ?? m.unit === 'money',
        permission: 'analytics:view',
        version: 1,
        policyVersion: `${since ?? V1}.${m.id}.1`,
        internalOnly: m.internalOnly ?? false,
    };
}
/** D531: a metric of the new analyses - catalog v2, platform admin only until D533 vets it for a partner. */
const adminMetric = (m) => metric({ ...m, since: V2, adminOnly: true });
/** D533: a metric built for the STORE's portal - catalog v3, admin-only except for a merchant tenant (see `merchant-review.ts`: every one is listed there with its reason). */
const merchantMetric = (m) => metric({ ...m, since: CATALOG_VERSION, adminOnly: true });
/** A money metric that is a plain sum over a money column of the aggregates and of `fact_money`. */
function moneySum(id, column) {
    return metric({ id, unit: 'money', additivity: 'additive', formulas: sources({ ...onAggs(single(sum(column))), fact_money: single(sum(column, isBooking)) }) });
}
/** D531: the orders where both the promised and the actual pickup exist (the only ones that can be compared); food has no promised time. */
const bothEta = all({ col: 'actual_pickup_seconds', notNull: true }, { col: 'promised_pickup_seconds', notNull: true });
const foodDone = all(eq('vertical', 'FOOD'), eq('outcome', V.outcome.completed));
/** A ride that came from a quote (the funnel's population). */
const fromQuote = all(isRide, { col: 'became_from_quote_id', notNull: true });
// D533 (the store's portal): a food order, the store's refusal, and who cancelled (the cancellation reason's actor, `dim_cancel_reason.actor_kind`)
const isFood = eq('vertical', 'FOOD');
const wasRefused = all(isFood, eq('status', 'REJECTED'));
const accepted = all(isFood, { col: 'accept_seconds', notNull: true });
const cancelledBy = (...actors) => all(isFood, eq('status', 'CANCELLED'), { via: { join: 'cancel', col: 'actor_kind' }, in: actors });
const completedFood = all(isFood, eq('outcome', V.outcome.completed));
const lostFood = all(isFood, eq('outcome', V.outcome.cancelled));
export const METRICS = [
    // ---- volume ----
    metric({ id: 'orders', unit: 'count', additivity: 'additive', formulas: sources({ ...onAggs(single(sum('orders_count'))), fact_order: single(count()) }) }),
    metric({ id: 'orders_completed', unit: 'count', additivity: 'additive', formulas: sources({ ...onAggs(single(sum('completed_count'))), fact_order: single(count(eq('outcome', V.outcome.completed))) }) }),
    metric({ id: 'orders_cancelled', unit: 'count', additivity: 'additive', formulas: sources({ ...onAggs(single(sum('cancelled_count'))), fact_order: single(count(eq('outcome', V.outcome.cancelled))) }) }),
    metric({
        id: 'completion_rate',
        unit: 'ratio',
        additivity: 'ratio',
        scale: 'ratio',
        formulas: sources({ ...onAggs(ratio(sum('completed_count'), sum('orders_count'))), fact_order: ratio(count(eq('outcome', V.outcome.completed)), count()) }),
    }),
    metric({
        id: 'cancellation_rate',
        unit: 'ratio',
        additivity: 'ratio',
        scale: 'ratio',
        formulas: sources({ ...onAggs(ratio(sum('cancelled_count'), sum('orders_count'))), fact_order: ratio(count(eq('outcome', V.outcome.cancelled)), count()) }),
    }),
    metric({ id: 'items_sold', unit: 'count', additivity: 'additive', formulas: sources({ ...onAggs(single(sum('items_count'))), fact_order: single(sum('items_count')) }) }),
    // ---- money (always per currency) ----
    moneySum('gmv', 'gross_minor'),
    moneySum('merchandise', 'merchandise_minor'),
    moneySum('delivery_fees', 'delivery_minor'),
    moneySum('discounts', 'discount_minor'),
    moneySum('commission', 'commission_minor'),
    moneySum('partner_net', 'partner_net_minor'),
    metric({
        id: 'take_rate',
        unit: 'ratio',
        additivity: 'ratio',
        scale: 'ratio',
        currencyBound: true,
        formulas: sources({ ...onAggs(ratio(sum('commission_minor'), sum('gross_minor'))), fact_money: ratio(sum('commission_minor', isBooking), sum('gross_minor', isBooking)) }),
    }),
    metric({
        id: 'average_ticket',
        unit: 'money',
        additivity: 'ratio',
        scale: 'money',
        formulas: sources({ ...onAggs(ratio(sum('gross_minor'), sum('completed_count'))) }),
    }),
    // ---- time and distance ----
    metric({ id: 'distance', unit: 'distance_m', additivity: 'additive', formulas: sources({ ...onAggs(single(sum('distance_meters_sum'))), fact_order: single(sum('distance_meters')) }) }),
    metric({
        id: 'delivery_time',
        unit: 'duration_s',
        additivity: 'ratio',
        scale: 'seconds',
        formulas: sources({ ...onAggs(ratio(sum('total_seconds_sum'), sum('total_samples'))), fact_order: ratio(sum('total_seconds'), count(undefined, 'total_seconds')) }),
    }),
    metric({
        id: 'prep_time',
        unit: 'duration_s',
        additivity: 'ratio',
        scale: 'seconds',
        formulas: sources({ ...onAggs(ratio(sum('prep_seconds_sum'), sum('prep_samples'))), fact_order: ratio(sum('prep_seconds'), count(undefined, 'prep_seconds')) }),
    }),
    // ---- rides ----
    metric({ id: 'rides_requested', unit: 'count', additivity: 'additive', formulas: sources({ ...onAggs(single(sum('orders_count', isRide)), AGG_WITH_VERTICAL), fact_order: single(count(isRide)) }) }),
    metric({ id: 'rides_completed', unit: 'count', additivity: 'additive', formulas: sources({ ...onAggs(single(sum('completed_count', isRide)), AGG_WITH_VERTICAL), fact_order: single(count(all(isRide, eq('outcome', V.outcome.completed)))) }) }),
    metric({ id: 'rides_no_driver', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(all(isRide, eq('outcome', V.outcome.noDriver)))) }) }),
    metric({
        id: 'assignment_rate',
        unit: 'ratio',
        additivity: 'ratio',
        scale: 'ratio',
        formulas: sources({ fact_order: ratio(count(all(isRide, { col: 'driver_key', notNull: true })), count(isRide)) }),
    }),
    metric({
        id: 'time_to_assign',
        unit: 'duration_s',
        additivity: 'ratio',
        scale: 'seconds',
        formulas: sources({ fact_order: ratio(sum('accept_seconds', isRide), count(isRide, 'accept_seconds')) }),
    }),
    // ---- distinct people: INTERNAL only, fact path only, non-additive ----
    metric({ id: 'active_customers', unit: 'count', additivity: 'non_additive', internalOnly: true, formulas: sources({ fact_order: single({ fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' } }) }) }),
    metric({ id: 'active_drivers', unit: 'count', additivity: 'non_additive', internalOnly: true, formulas: sources({ fact_order: single({ fn: 'distinct', via: { join: 'driver', col: 'pseudonymous_key' } }) }) }),
    metric({ id: 'active_couriers', unit: 'count', additivity: 'non_additive', internalOnly: true, formulas: sources({ fact_order: single({ fn: 'distinct', via: { join: 'courier', col: 'pseudonymous_key' } }) }) }),
    // ---- partner spend (the charge ledger's two states) ----
    metric({ id: 'partner_spend_settled', unit: 'money', additivity: 'additive', formulas: sources({ fact_money: single(sum('amount_minor', eq('source_kind', V.sourceKind.partnerChargeSettled))) }) }),
    metric({ id: 'partner_spend_authorized', unit: 'money', additivity: 'additive', formulas: sources({ fact_money: single(sum('amount_minor', eq('source_kind', V.sourceKind.partnerChargeAuthorized))) }) }),
    // ---- sold lines ----
    metric({ id: 'item_quantity', unit: 'count', additivity: 'additive', formulas: sources({ fact_order_item: single(sum('quantity')) }) }),
    metric({ id: 'item_revenue', unit: 'money', additivity: 'additive', formulas: sources({ fact_order_item: single(sum('amount_minor')) }) }),
    // ======================================================================================================================================
    // D531 (AN-ADMIN-INSIGHTS): the market-standard analyses. Every one is admin-only (D533 reviews them for partners one by one).
    // ======================================================================================================================================
    // ---- time per stage: request -> accept, accept -> pickup, pickup -> drop (prep_time exists) ----
    adminMetric({ id: 'accept_time', unit: 'duration_s', additivity: 'ratio', scale: 'seconds', formulas: sources({ fact_order: ratio(sum('accept_seconds'), count(undefined, 'accept_seconds')) }) }),
    adminMetric({ id: 'pickup_time', unit: 'duration_s', additivity: 'ratio', scale: 'seconds', formulas: sources({ fact_order: ratio(sum('actual_pickup_seconds'), count(undefined, 'actual_pickup_seconds')) }) }),
    adminMetric({ id: 'trip_time', unit: 'duration_s', additivity: 'ratio', scale: 'seconds', formulas: sources({ fact_order: ratio(sum('transit_seconds'), count(undefined, 'transit_seconds')) }) }),
    // ---- promised against actual pickup: the orders where both exist, the share that came on time (within the grace), and the signed mean deviation ----
    adminMetric({ id: 'eta_compared', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(bothEta)) }) }),
    adminMetric({
        id: 'eta_on_time_rate',
        unit: 'ratio',
        additivity: 'ratio',
        scale: 'ratio',
        formulas: sources({ fact_order: ratio(count({ cols: ['actual_pickup_seconds', 'promised_pickup_seconds'], le: V.eta.onTimeGraceSeconds }), count(bothEta)) }),
    }),
    adminMetric({
        id: 'eta_deviation',
        unit: 'delta_s',
        additivity: 'ratio',
        scale: 'seconds',
        formulas: sources({ fact_order: ratio({ fn: 'sum', arith: { op: '-', a: 'actual_pickup_seconds', b: 'promised_pickup_seconds' } }, count(bothEta)) }),
    }),
    // ---- the basket and the frequency ----
    adminMetric({ id: 'basket_size', unit: 'decimal', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_order: ratio(sum('items_count', foodDone), count(foodDone)) }) }),
    adminMetric({ id: 'orders_per_customer', unit: 'decimal', additivity: 'ratio', internalOnly: true, scale: 'ratio', formulas: sources({ fact_order: ratio(count(), { fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' } }) }) }),
    // ---- new against returning (a customer is NEW on the day of the first order, RETURNING on every later day) ----
    adminMetric({ id: 'new_customers', unit: 'count', additivity: 'non_additive', internalOnly: true, formulas: sources({ fact_order: single({ fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' }, where: { firstOrder: 'first' } }) }) }),
    adminMetric({ id: 'returning_customers', unit: 'count', additivity: 'non_additive', internalOnly: true, formulas: sources({ fact_order: single({ fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' }, where: { firstOrder: 'later' } }) }) }),
    adminMetric({ id: 'first_orders', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count({ firstOrder: 'first' })) }) }),
    adminMetric({ id: 'repeat_orders', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count({ firstOrder: 'later' })) }) }),
    // ---- value lost: the quoted fare of a cancelled ride / of a ride nobody took (rides only: food and delivery record no quoted value) ----
    adminMetric({ id: 'cancelled_value', unit: 'money', additivity: 'additive', formulas: sources({ fact_order: single(sum('quoted_minor', eq('outcome', V.outcome.cancelled))) }) }),
    adminMetric({ id: 'no_driver_value', unit: 'money', additivity: 'additive', formulas: sources({ fact_order: single(sum('quoted_minor', eq('outcome', V.outcome.noDriver))) }) }),
    adminMetric({ id: 'quoted_value', unit: 'money', additivity: 'additive', formulas: sources({ fact_order: single(sum('quoted_minor')) }) }),
    // ---- the ride funnel, from the rides that CAME FROM a quote (so every step is a subset of the one before) ----
    adminMetric({ id: 'funnel_requested', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(fromQuote)) }) }),
    adminMetric({ id: 'funnel_matched', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(all(fromQuote, { col: 'driver_key', notNull: true }))) }) }),
    adminMetric({ id: 'funnel_completed', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(all(fromQuote, eq('outcome', V.outcome.completed)))) }) }),
    adminMetric({ id: 'rides_matched', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(all(isRide, { col: 'driver_key', notNull: true }))) }) }),
    adminMetric({ id: 'quotes', unit: 'count', additivity: 'additive', formulas: sources({ fact_quote: single(count()) }) }),
    adminMetric({ id: 'quotes_converted', unit: 'count', additivity: 'additive', formulas: sources({ fact_quote: single(count(eq('became_ride', 'true'))) }) }),
    adminMetric({ id: 'quote_conversion_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_quote: ratio(count(eq('became_ride', 'true')), count()) }) }),
    adminMetric({ id: 'quoted_total', unit: 'money', additivity: 'additive', formulas: sources({ fact_quote: single(sum('total_minor')) }) }),
    // ---- the dispatch offers: who decided what (an offer superseded or cancelled before the driver saw it is not a decision) ----
    adminMetric({ id: 'offers', unit: 'count', additivity: 'additive', formulas: sources({ fact_offer: single(count()) }) }),
    adminMetric({ id: 'offers_decided', unit: 'count', additivity: 'additive', formulas: sources({ fact_offer: single(count({ col: 'status', in: V.offerDecided })) }) }),
    adminMetric({ id: 'offers_accepted', unit: 'count', additivity: 'additive', formulas: sources({ fact_offer: single(count(eq('status', V.offerStatus.accepted))) }) }),
    adminMetric({ id: 'offers_declined', unit: 'count', additivity: 'additive', formulas: sources({ fact_offer: single(count(eq('status', V.offerStatus.rejected))) }) }),
    adminMetric({ id: 'offers_lapsed', unit: 'count', additivity: 'additive', formulas: sources({ fact_offer: single(count(eq('status', V.offerStatus.expired))) }) }),
    adminMetric({ id: 'offer_acceptance_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_offer: ratio(count(eq('status', V.offerStatus.accepted)), count({ col: 'status', in: V.offerDecided })) }) }),
    adminMetric({ id: 'offer_decline_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_offer: ratio(count(eq('status', V.offerStatus.rejected)), count({ col: 'status', in: V.offerDecided })) }) }),
    adminMetric({ id: 'offer_lapse_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_offer: ratio(count(eq('status', V.offerStatus.expired)), count({ col: 'status', in: V.offerDecided })) }) }),
    // ---- ratings ----
    adminMetric({ id: 'ratings', unit: 'count', additivity: 'additive', formulas: sources({ fact_rating: single(count()) }) }),
    adminMetric({ id: 'rating_average', unit: 'decimal', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_rating: ratio(sum('stars'), count()) }) }),
    adminMetric({ id: 'low_ratings', unit: 'count', additivity: 'additive', formulas: sources({ fact_rating: single(count({ col: 'stars', in: V.lowRatingStars })) }) }),
    adminMetric({ id: 'low_rating_share', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_rating: ratio(count({ col: 'stars', in: V.lowRatingStars }), count()) }) }),
    // ---- coupons ----
    adminMetric({ id: 'promo_redemptions', unit: 'count', additivity: 'additive', formulas: sources({ fact_promo_redemption: single(count()) }) }),
    adminMetric({ id: 'promo_discount', unit: 'money', additivity: 'additive', formulas: sources({ fact_promo_redemption: single(sum('discount_minor')) }) }),
    adminMetric({ id: 'promo_gross', unit: 'money', additivity: 'additive', formulas: sources({ fact_promo_redemption: single(sum('gross_minor')) }) }),
    adminMetric({ id: 'promo_subsidy', unit: 'money', additivity: 'additive', formulas: sources({ fact_promo_redemption: single(sum('subsidy_minor')) }) }),
    adminMetric({ id: 'promo_discount_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', currencyBound: true, formulas: sources({ fact_promo_redemption: ratio(sum('discount_minor'), sum('gross_minor')) }) }),
    adminMetric({ id: 'promo_customers', unit: 'count', additivity: 'non_additive', internalOnly: true, formulas: sources({ fact_promo_redemption: single({ fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' } }) }) }),
    // ---- problems and refunds ----
    adminMetric({ id: 'issues', unit: 'count', additivity: 'additive', formulas: sources({ fact_issue: single(count()) }) }),
    adminMetric({ id: 'issues_resolved', unit: 'count', additivity: 'additive', formulas: sources({ fact_issue: single(count(undefined, 'resolved_at')) }) }),
    adminMetric({ id: 'issue_amount', unit: 'money', additivity: 'additive', formulas: sources({ fact_issue: single(sum('amount_minor')) }) }),
    adminMetric({ id: 'refunds', unit: 'count', additivity: 'additive', formulas: sources({ fact_issue: single(count({ col: 'kind', in: V.refundKinds })) }) }),
    adminMetric({ id: 'refund_amount', unit: 'money', additivity: 'additive', formulas: sources({ fact_issue: single(sum('amount_minor', { col: 'kind', in: V.refundKinds })) }) }),
    // ---- store availability (closed intervals only: the interval that is still running has no length yet) ----
    adminMetric({ id: 'store_live_seconds', unit: 'duration_s', additivity: 'additive', formulas: sources({ fact_store_availability: single(sum('seconds', { col: 'state', in: V.storeLiveStates })) }) }),
    adminMetric({ id: 'store_open_seconds', unit: 'duration_s', additivity: 'additive', formulas: sources({ fact_store_availability: single(sum('seconds', { col: 'state', in: V.storeOpenStates })) }) }),
    adminMetric({ id: 'store_paused_seconds', unit: 'duration_s', additivity: 'additive', formulas: sources({ fact_store_availability: single(sum('seconds', eq('state', V.storeAvailabilityState.paused))) }) }),
    adminMetric({ id: 'store_pauses', unit: 'count', additivity: 'additive', formulas: sources({ fact_store_availability: single(count(eq('state', V.storeAvailabilityState.paused))) }) }),
    adminMetric({
        id: 'store_availability_rate',
        unit: 'ratio',
        additivity: 'ratio',
        scale: 'ratio',
        formulas: sources({ fact_store_availability: ratio(sum('seconds', { col: 'state', in: V.storeOpenStates }), sum('seconds', { col: 'state', in: V.storeLiveStates })) }),
    }),
    // ---- supply, from the sampled presence (the k-floored market x hour view: never a driver) ----
    adminMetric({ id: 'supply_online_time', unit: 'duration_s', additivity: 'additive', formulas: sources({ agg_presence_market_hour: single(sum('seconds_online')) }) }),
    adminMetric({ id: 'supply_idle_time', unit: 'duration_s', additivity: 'additive', formulas: sources({ agg_presence_market_hour: single(sum('seconds_available')) }) }),
    adminMetric({ id: 'supply_busy_time', unit: 'duration_s', additivity: 'additive', formulas: sources({ agg_presence_market_hour: single({ fn: 'sum', arith: { op: '+', a: 'seconds_to_pickup', b: 'seconds_on_trip' } }) }) }),
    adminMetric({ id: 'supply_driver_hours', unit: 'count', additivity: 'additive', formulas: sources({ agg_presence_market_hour: single(sum('drivers_online')) }) }),
    adminMetric({ id: 'supply_avg_drivers', unit: 'decimal', additivity: 'ratio', scale: 'ratio', formulas: sources({ agg_presence_market_hour: ratio(sum('drivers_online'), count()) }) }),
    adminMetric({
        id: 'supply_utilisation',
        unit: 'ratio',
        additivity: 'ratio',
        scale: 'ratio',
        formulas: sources({ agg_presence_market_hour: ratio({ fn: 'sum', arith: { op: '+', a: 'seconds_to_pickup', b: 'seconds_on_trip' } }, sum('seconds_online')) }),
    }),
    // ---- demand by pickup cell (the k-floored cell-day view) ----
    adminMetric({ id: 'cell_orders', unit: 'count', additivity: 'additive', formulas: sources({ agg_pickup_cell_day: single(sum('orders_count')) }) }),
    // ======================================================================================================================================
    // D533 (AN-MERCHANT-V2): the store's portal. Catalog v3; admin-only like the D531 block, each one admitted for a MERCHANT tenant by `merchant-review.ts`.
    // ======================================================================================================================================
    // ---- what became of the orders the store received (food) ----
    merchantMetric({ id: 'refused_orders', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(wasRefused)) }) }),
    merchantMetric({ id: 'auto_refused_orders', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(all(wasRefused, { via: { join: 'cancel', col: 'actor_kind' }, in: ['SYSTEM'] }))) }) }),
    merchantMetric({ id: 'store_cancelled_orders', unit: 'count', additivity: 'additive', formulas: sources({ fact_order: single(count(cancelledBy('MERCHANT'))) }) }),
    // accepted / (accepted + refused): the orders the store DECIDED on (an order the customer withdrew before the store answered, or one still waiting, is no decision)
    merchantMetric({ id: 'store_acceptance_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_order: ratio(count(accepted), count(all(isFood, { any: [{ col: 'accept_seconds', notNull: true }, eq('status', 'REJECTED')] }))) }) }),
    merchantMetric({ id: 'store_refusal_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_order: ratio(count(wasRefused), count(isFood)) }) }),
    merchantMetric({ id: 'store_cancel_rate', unit: 'ratio', additivity: 'ratio', scale: 'ratio', formulas: sources({ fact_order: ratio(count(cancelledBy('MERCHANT')), count(isFood)) }) }),
    // ---- value: the items of the orders, by what became of them (food only: a ride's value is its fare, a delivery has no items value) ----
    merchantMetric({ id: 'lost_order_value', unit: 'money', additivity: 'additive', formulas: sources({ fact_order: single(sum('order_value_minor', lostFood)) }) }),
    merchantMetric({ id: 'refused_order_value', unit: 'money', additivity: 'additive', formulas: sources({ fact_order: single(sum('order_value_minor', wasRefused)) }) }),
    merchantMetric({ id: 'sold_order_value', unit: 'money', additivity: 'additive', formulas: sources({ fact_order: single(sum('order_value_minor', completedFood)) }) }),
    // ---- the counter: ready -> picked up (the order waiting for the courier) ----
    merchantMetric({ id: 'pickup_wait', unit: 'duration_s', additivity: 'ratio', scale: 'seconds', formulas: sources({ fact_order: ratio(sum('pickup_wait_seconds'), count(undefined, 'pickup_wait_seconds')) }) }),
    // ---- the sold lines of DELIVERED orders only (item_quantity / item_revenue also count the lines of cancelled orders) ----
    merchantMetric({ id: 'sold_item_quantity', unit: 'count', additivity: 'additive', formulas: sources({ fact_order_item: single(sum('quantity', eq('outcome', V.outcome.completed))) }) }),
    merchantMetric({ id: 'sold_item_revenue', unit: 'money', additivity: 'additive', formulas: sources({ fact_order_item: single(sum('amount_minor', eq('outcome', V.outcome.completed))) }) }),
];
/**
 * D528: the SUPPORT of a group - how many underlying records it rests on (an hour bucket's `orders_count`, a fact row each). It is NOT in
 * {@link METRICS}: a request cannot name it. Only the partner path asks for it (`PlanContext.support`), to apply the k-anonymity floor to the
 * groups AFTER the filters, and it never leaves the server (`suppressForPartner` removes it).
 */
export const SUPPORT_METRIC = metric({
    id: 'record_support',
    unit: 'count',
    additivity: 'additive',
    formulas: sources({
        ...onAggs(single(sum('orders_count')), ['agg_market_hour', 'agg_merchant_hour', 'agg_partner_hour']),
        fact_order: single(count()),
        fact_money: single(count()),
        fact_order_item: single(count()),
        // D531: the new sources count their records too (the values lister counts per value); the two views count what they publish.
        fact_offer: single(count()),
        fact_quote: single(count()),
        fact_rating: single(count()),
        fact_promo_redemption: single(count()),
        fact_issue: single(count()),
        fact_store_availability: single(count()),
        agg_presence_market_hour: single(count()),
        agg_pickup_cell_day: single(sum('orders_count')),
    }),
});
/**
 * D528 review P2-1: the hidden PEOPLE support of a group - how many distinct pseudonymous customers it rests on (`COUNT(DISTINCT dc.pseudonymous_key)`).
 * Like {@link SUPPORT_METRIC} it is not in {@link METRICS} and never leaves the server; the partner path adds it so the floor holds for people AND
 * for records. It lives on the two facts that carry the customer (`fact_order`, `fact_money`), so a partner question is answered from them.
 */
export const SUBJECT_SUPPORT_METRIC = metric({
    id: 'subject_support',
    unit: 'count',
    additivity: 'non_additive',
    internalOnly: true,
    formulas: sources({
        fact_order: single({ fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' } }),
        fact_money: single({ fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' } }),
        // D533 (a0014): the sold lines carry the order's customer, so the people floor holds for an item as it does for an order
        fact_order_item: single({ fn: 'distinct', via: { join: 'customer', col: 'pseudonymous_key' } }),
    }),
});
/**
 * D533 review P3: the hidden COURIER support of a group - how many distinct couriers its trips rest on. A trip time (and the total time that holds it, and a
 * bucket of either) is the courier's own speed; in a small town a cell can be ONE courier's, and the store would read a person's performance. Like
 * {@link SUBJECT_SUPPORT_METRIC} it is not in {@link METRICS}, never leaves the server, and the partner path adds it only to a request that touches a time that
 * contains the trip. A food order with NO courier recorded counts as zero couriers: a cell that cannot show five is withheld, never assumed.
 */
export const COURIER_SUPPORT_METRIC = metric({
    id: 'courier_support',
    unit: 'count',
    additivity: 'non_additive',
    internalOnly: true,
    formulas: sources({
        fact_order: single({ fn: 'distinct', via: { join: 'courier', col: 'pseudonymous_key' } }),
    }),
});
/** The metrics and the dimensions whose figure contains the courier's trip: asking for one of them puts the courier floor on the answer. */
export const COURIER_SENSITIVE_METRICS = new Set(['trip_time', 'delivery_time']);
export const COURIER_SENSITIVE_DIMENSIONS = new Set(['delivery_time_bucket', 'total_time_bucket']);
const METRIC_INDEX = new Map(METRICS.map((m) => [m.id, m]));
export function getMetric(id) {
    return METRIC_INDEX.get(id);
}
// ───────────────────────────── helpers ─────────────────────────────
/** The joins a key expression needs. */
export function joinsOfKey(key) {
    if (key.kind === 'via')
        return [key.join];
    if (key.kind === 'dayPart')
        return ['hour'];
    if (key.kind === 'holiday')
        return ['date'];
    if (key.kind === 'customerType' || key.kind === 'cohort')
        return ['customer'];
    if (key.kind === 'ownedItem')
        return ['item', 'merchant'];
    return [];
}
/** The joins a predicate needs (the first-order tests read the customer's first-order date). */
export function joinsOfPred(pred) {
    if (pred === undefined)
        return [];
    if ('all' in pred)
        return pred.all.flatMap((p) => joinsOfPred(p));
    if ('any' in pred)
        return pred.any.flatMap((p) => joinsOfPred(p));
    if ('via' in pred)
        return [pred.via.join];
    return 'firstOrder' in pred ? ['customer'] : [];
}
/** The joins a piece needs (a distinct count reads a joined attribute; a first-order predicate reads the customer). */
export function joinsOfPiece(piece) {
    return [...(piece.via !== undefined ? [piece.via.join] : []), ...joinsOfPred(piece.where)];
}
export function formulaPieces(formula) {
    return formula.kind === 'single' ? [formula.piece] : [formula.num, formula.den];
}
export function predColumns(pred) {
    if (pred === undefined)
        return [];
    if ('all' in pred)
        return pred.all.flatMap((p) => predColumns(p));
    if ('any' in pred)
        return pred.any.flatMap((p) => predColumns(p));
    if ('via' in pred)
        return [];
    if ('cols' in pred)
        return [...pred.cols];
    if ('firstOrder' in pred)
        return ['date_key'];
    return [pred.col];
}
/** The dimension ids a metric can be cut by on at least one of its sources (a guide for screens; the planner is the authority). */
export function dimensionsFor(metricId) {
    const m = getMetric(metricId);
    if (m === undefined)
        return [];
    const sourcesOfMetric = Object.keys(m.formulas);
    return DIMENSIONS.filter((d) => sourcesOfMetric.some((s) => d.sources[s] !== undefined)).map((d) => d.id);
}
/** What a caller may see: personal dimensions only with `analytics:view_personal`; internal-only metrics never on a partner surface. */
export function visibleCatalog(access) {
    const audience = access.audience ?? 'internal';
    const reviewed = (kind, id) => audience === 'partner' && access.reviewedFor === 'merchant' && isMerchantReviewed(kind, id);
    const dims = DIMENSIONS.filter((d) => (access.personal || !d.personal) && (audience === 'internal' || d.adminOnly !== true || reviewed('dimension', d.id)));
    const ids = new Set(dims.map((d) => d.id));
    const visibleMetrics = METRICS.filter((m) => audience === 'internal' || (!m.internalOnly && (m.adminOnly !== true || reviewed('metric', m.id))));
    const sourcesOf = (m) => new Set(Object.keys(m.formulas));
    const spans = (a, b) => {
        const bs = sourcesOf(b);
        return ![...sourcesOf(a)].some((s) => bs.has(s));
    };
    return {
        version: CATALOG_VERSION,
        metrics: visibleMetrics.map((m) => ({
            id: m.id,
            labelKey: m.labelKey,
            descriptionKey: m.descriptionKey,
            unit: m.unit,
            additivity: m.additivity,
            currencyBound: m.currencyBound,
            version: m.version,
            policyVersion: m.policyVersion,
            dimensions: dimensionsFor(m.id).filter((d) => ids.has(d)),
            refused: {
                dimensions: access.personal
                    ? []
                    : dimensionsFor(m.id)
                        .filter((d) => !ids.has(d) && getDimension(d)?.personal === true)
                        .map((d) => ({ id: d, code: m.internalOnly ? 'ANALYTICS_LINKAGE_FORBIDDEN' : 'ANALYTICS_PERSONAL_FORBIDDEN' })),
                metrics: visibleMetrics.filter((o) => o !== m && spans(m, o)).map((o) => ({ id: o.id, code: 'METRICS_SPAN_FACT_TABLES' })),
            },
        })),
        dimensions: dims.map((d) => ({
            id: d.id,
            labelKey: d.labelKey,
            kind: d.kind,
            valueType: d.valueType,
            personal: d.personal,
            filterOnly: d.filterOnly === true,
            operators: operatorsFor(d),
            enumerable: isEnumerable(d),
            ...(d.hierarchy !== undefined ? { hierarchy: d.hierarchy } : {}),
        })),
    };
}
//# sourceMappingURL=catalog.js.map